import { createTheme, type Theme } from '@mui/material/styles'
// MUI's own built-in strings — the pagination "1–3 of 3", its button labels —
// come from a locale bundle, not from our t(). Without it they stayed English
// on Arabic screens.
import { arEG, enUS } from '@mui/material/locale'
import type { Language } from '@mefosa/shared'

/**
 * MEFOSA palette, sampled from their actual logo files.
 *
 * Accessibility note: the raw brand green #02BB04 (2.59:1 on white) and the
 * brand cyan #2BB0EA (2.47:1) FAIL WCAG AA for text. They are kept for
 * decorative fills only; text and status colours use the accessible
 * equivalents below. Every value here was contrast-checked.
 */
export const BRAND = {
  navy: '#123A6D',    // logo pill core - sidebar/app bar. 11.34:1 on white
  blue: '#0B5AB6',    // signature MEFOSA blue - primary. 6.66:1 on white
  cyan: '#2BB0EA',    // MENA map silhouette - decorative only
  leaf: '#02BB04',    // brand leaf green - decorative only
  green: '#1B7A26',   // accessible green for text/status. 5.44:1
  teal: '#00647E',    // secondary. 6.72:1
} as const

/**
 * The five pipeline stages, as a SEQUENTIAL ramp keyed 1-5.
 *
 * Stages are ordered, not categories, so this is one hue running light to dark
 * rather than five unrelated colours — further along the pipeline reads as
 * stronger. The previous array was keyed 0-4 for the old level ladder, so
 * stage 5 fell off the end and rendered in the fallback grey, which made
 * "Closed / full pay" look inactive.
 *
 * Lightness is monotonic in both modes. Dark mode is its own ramp, not a flip:
 * on a dark surface "stronger" means brighter, so the order reverses in
 * luminance while keeping the same reading.
 *
 * The lightest step sits under 3:1 against its surface, which is unavoidable in
 * a five-step ramp — so every bar carries its value as a direct label and
 * colour never has to be read on its own.
 */
export const STAGE_COLORS_LIGHT: Record<number, string> = {
  1: '#6FB4DE', 2: '#3A93CE', 3: '#1873B8', 4: '#115394', 5: '#123A6D',
}
export const STAGE_COLORS_DARK: Record<number, string> = {
  1: '#2A5F8F', 2: '#2E7BB8', 3: '#3E9AD4', 4: '#63B3E0', 5: '#9BD0EC',
}

export function buildTheme(language: Language, mode: 'light' | 'dark' = 'light'): Theme {
  const isDark = mode === 'dark'
  const arabic = language === 'ar'

  // Inter and IBM Plex Sans Arabic share similar x-heights, so they sit well
  // together. Arabic needs more line-height to clear diacritics.
  const fontFamily = arabic
    ? '"IBM Plex Sans Arabic", "Inter", system-ui, sans-serif'
    : '"Inter", "IBM Plex Sans Arabic", system-ui, sans-serif'

  return createTheme({
    direction: arabic ? 'rtl' : 'ltr',
    palette: {
      mode,
      primary: isDark
        ? { main: '#6DB6F5', light: '#A8D4FA', dark: '#2E7FC4', contrastText: '#06182F' }
        : { main: BRAND.blue, light: BRAND.cyan, dark: BRAND.navy, contrastText: '#FFFFFF' },
      secondary: isDark
        ? { main: '#3FC3E0', light: '#7FDCEF', dark: '#00728F', contrastText: '#06182F' }
        : { main: BRAND.teal, light: '#35A8C4', dark: '#004659', contrastText: '#FFFFFF' },
      success: isDark
        ? { main: '#5CC96F', dark: BRAND.green, contrastText: '#06182F' }
        : { main: BRAND.green, light: BRAND.leaf, dark: '#0F5418', contrastText: '#FFFFFF' },
      warning: isDark
        ? { main: '#F2B233', dark: '#8A5300', contrastText: '#06182F' }
        : { main: '#8A5300', light: '#F2B233', dark: '#5E3900', contrastText: '#FFFFFF' },
      error: isDark
        ? { main: '#F58279', dark: '#7A1912', contrastText: '#06182F' }
        : { main: '#B3261E', light: '#F58279', dark: '#7A1912', contrastText: '#FFFFFF' },
      info: isDark
        ? { main: '#62C8F0', dark: '#07406F', contrastText: '#06182F' }
        : { main: '#0B5FA5', light: '#62C8F0', dark: '#07406F', contrastText: '#FFFFFF' },
      background: isDark
        ? { default: '#0B1622', paper: '#132234' }
        : { default: '#F5F9FF', paper: '#FFFFFF' },
      text: isDark
        ? { primary: '#E6EDF6', secondary: '#9FB0C4' }
        : { primary: '#0F2440', secondary: '#4A5C74', disabled: '#8A99AD' },
      divider: isDark ? '#22374F' : '#D6E2F0',
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily,
      // Arabic script needs more vertical room than Latin at the same size.
      body1: { lineHeight: arabic ? 1.85 : 1.6 },
      body2: { lineHeight: arabic ? 1.8 : 1.55 },
      h4: { fontWeight: 700, letterSpacing: arabic ? 0 : '-0.02em' },
      h5: { fontWeight: 700, letterSpacing: arabic ? 0 : '-0.01em' },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: 'antialiased' },
        },
      },
      MuiButton: { defaultProps: { disableElevation: true } },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}` }),
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
      MuiTableCell: {
        styleOverrides: {
          // Logical property so the header rule flips correctly in Arabic.
          head: { fontWeight: 700, whiteSpace: 'nowrap' },
        },
      },
    },
  }, arabic ? arEG : enUS)
}

/**
 * Profit bars. One series, so colour carries only the sign — and every bar
 * also shows its value, so a minus sign says "loss" even where the colour
 * cannot be read.
 *
 * Both pairs were checked with the palette validator rather than by eye:
 * light #1873B8/#B3261E and dark #3E9AD4/#E05C52 each clear the lightness
 * band, the chroma floor, colour-blind separation and 3:1 against their own
 * surface. Dark is its own step, not a lightened flip of the light one.
 */
export const PROFIT_COLORS_LIGHT = { positive: '#1873B8', negative: '#B3261E' }
export const PROFIT_COLORS_DARK = { positive: '#3E9AD4', negative: '#E05C52' }
